import {
    createCustomer,
    joinCustomer,
    updateCustomer,
} from 'shared/api/v2/customers/customers.client';
import {
    CustomerStatus,
    TCustomerStatus,
} from 'shared/api/v2/customers/customers.types';
import { TSourceKey, TUTMMediumKey } from 'shared/types';
import { ErrorMessages } from 'shared/constants/errorMessages';

type CreateOrJoinCustomerParams = {
    customerStatus: TCustomerStatus | undefined;
    customerId: string | null;
    merchantId: string;
    email?: string;
    phoneNumber: string;
    source: TSourceKey;
    medium?: TUTMMediumKey;
    name?: string;
    birthday?: string;
};

type CreateOrJoinCustomerResult = {
    success: boolean;
    error?: string;
    customerId?: string | null;
};

/**
 * Creates or joins a customer to a merchant location.
 * For NOT_FOUND: creates customer (automatically joined)
 * For NOT_CONNECTED/CONNECTED: updates customer then joins to merchant
 */
export const createOrJoinCustomerToLocation = async (
    params: CreateOrJoinCustomerParams
): Promise<CreateOrJoinCustomerResult> => {
    const {
        customerStatus,
        customerId,
        merchantId,
        email,
        phoneNumber,
        source,
        medium,
        name,
        birthday,
    } = params;

    const trimmedName = name?.trim();

    if (customerStatus === CustomerStatus.NOT_FOUND) {
        const { data: newCustomer, error: createCustomerError } =
            await createCustomer({
                merchant_id: merchantId,
                ...(email ? { email } : {}),
                source,
                medium: medium ?? 1,
                customer_info: {
                    phone_number: phoneNumber,
                    birthday: birthday?.length ? birthday : undefined,
                    full_name: trimmedName || undefined,
                },
            });

        if (createCustomerError || !newCustomer) {
            return {
                success: false,
                error: createCustomerError?.generic || ErrorMessages.DEFAULT,
            };
        }

        return {
            success: true,
            customerId: newCustomer.customer_id,
        };
    } else if (
        (customerStatus === CustomerStatus.NOT_CONNECTED ||
            customerStatus === CustomerStatus.CONNECTED) &&
        customerId
    ) {
        // Update customer first
        const { error: updateCustomerError } = await updateCustomer(
            customerId,
            {
                email: email?.length ? email : undefined,
                phone_number: phoneNumber,
                birthday: birthday?.length ? birthday : undefined,
                full_name: trimmedName || undefined,
            }
        );

        if (updateCustomerError) {
            return {
                success: false,
                error: ErrorMessages.DEFAULT,
            };
        }

        // Then join to merchant
        const { error: joinCustomerError } = await joinCustomer(customerId, {
            merchant_id: merchantId,
            source,
            medium: medium ?? 1,
        });

        if (joinCustomerError) {
            return {
                success: false,
                error: ErrorMessages.DEFAULT,
            };
        }

        return {
            success: true,
            customerId,
        };
    }

    return {
        success: false,
        error: ErrorMessages.DEFAULT,
    };
};
